import { Button, Collapse, Divider } from 'antd';
import { useHelpers } from '../../../providers/HelpersProvider';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { TaxBehavior } from '../../../services/exports/Constants';
import { useTranslation } from 'react-i18next';
import { DownOutlined } from '@ant-design/icons';
import classnames from 'classnames';

export default function OrderPaymentBreakdown({ order }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:AlertScreens:Shared:OrderPaymentBreakdown' });

  const { formatCurrency } = useHelpers();
  const [open, setOpen] = useState(false);

  return (
    <Collapse
      ghost
      expandIcon={() => null}
      onChange={(keys) => setOpen(keys.length > 0)}
      items={[
        {
          key: '1',
          classNames: {
            header: '!p-none w-full',
            body: '!p-none',
          },
          label: (
            <Button
              icon={
                <DownOutlined
                  className={classnames('transition-transform ease-in-out duration-150', { 'rotate-180': open })}
                />
              }
              className="w-full"
            >
              {t('title')}
            </Button>
          ),
          children: (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-sm mt-sm space-y-xs">
              <div className="flex justify-between">
                <span>{t('subtotal')}</span>
                <span>{formatCurrency(order.original_price)}</span>
              </div>
              {order.method_fee > 0 && (
                <div className="flex justify-between">
                  <span>{t('method_fee')}</span>
                  <span>{formatCurrency(order.method_fee)}</span>
                </div>
              )}
              {order.tip > 0 && (
                <div className="flex justify-between">
                  <span>{t('tip')}</span>
                  <span>{formatCurrency(order.tip)}</span>
                </div>
              )}
              {order.service_fee > 0 && (
                <div className="flex justify-between">
                  <span>{t('service_fee')}</span>
                  <span>{formatCurrency(order.service_fee)}</span>
                </div>
              )}
              {order.payment_method_upcharge_fee > 0 && (
                <div className="flex justify-between">
                  <span>{t('payment_method_upcharge_fee')}</span>
                  <span>{formatCurrency(order.payment_method_upcharge_fee)}</span>
                </div>
              )}
              {order.total_discount > 0 && <DiscountBreakdown data={order} />}
              {order.tax_behaviour === TaxBehavior.Exclusive && order.tax > 0 && (
                <div className="flex justify-between">
                  <span>{t('taxes')}</span>
                  <span>{formatCurrency(order.tax)}</span>
                </div>
              )}
              <Divider />
              <div className="flex justify-between">
                <span>{t('total')}</span>
                <span>{formatCurrency(order.total_amount)}</span>
              </div>
            </div>
          ),
        },
      ]}
    />
  );
}

const DiscountBreakdown = ({ data }) => {
  const { t: translate } = useTranslation(null, {
    keyPrefix: 'Components:Print:InnerPrinterReceipt.discount_breakdown',
  });
  const company = useSelector((state) => state.company.company);
  const lng = company?.legal_lang ?? company?.region?.default_locale;
  const { formatCurrency } = useHelpers();

  if (!data?.discount_breakdown) {
    return null;
  }

  const entries = Object.entries(data?.discount_breakdown);

  if (!entries?.length) {
    return null;
  }

  function t(key, config) {
    return translate(key, { lng, ...config });
  }

  return (
    <>
      <div className="flex justify-between">
        <span>{t('label')}</span>
        <span>-{formatCurrency(data.total_discount)}</span>
      </div>
      {entries.map((entry) => (
        <div className="flex justify-between ml-md" key={entry[0]}>
          <span>{t(entry[0])}</span>
          <span>-{formatCurrency(entry[1] / 100)}</span>
        </div>
      ))}
    </>
  );
};
